import {
  Invite,
  InviteValidationResponse,
  SendInviteRequest,
  RegisterRequest
} from '../types/invite';
import { AuthUser } from '../types/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const inviteApiService = {
  /**
   * Send an invitation email
   */
  sendInvite: async (data: SendInviteRequest): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include session cookie
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      throw error;
    }
  },

  /**
   * Validate an invite token
   */
  validateInvite: async (token: string): Promise<InviteValidationResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/invite/${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to validate invite');
      }

      return data.invite;
    } catch (error) {
      console.error('Error validating invite:', error);
      throw error;
    }
  },

  /**
   * Register with invite token
   */
  register: async (data: RegisterRequest): Promise<{ user: AuthUser }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Receive session cookie
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Registration failed');
      }

      return result;
    } catch (error) {
      console.error('Error registering:', error);
      throw error;
    }
  },

  /**
   * Fetch all invites (for admin)
   */
  fetchInvites: async (): Promise<Invite[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/invites`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch invites');
      }

      return data.invites;
    } catch (error) {
      console.error('Error fetching invites:', error);
      throw error;
    }
  },
};
